import { all, call, fork, put, select, takeLatest, take } from 'redux-saga/effects';
import { updateState } from 'core/actions/product/state';
import { decrementIndex, INext, IVerificationRegister, next, prev } from 'core/slices/register';
import { EntityTypes } from 'constants/entities';
import { selectActionFeed, IActionFeed } from 'core/selectors/product/action/actionFeed';
import { RootState } from 'types';
import { selectToken } from 'core/selectors/product/token';
import { selectVerificationRegister } from 'core/selectors/product/verificationRegister';
import { verificationRequest } from 'core/services/product';
import { IToken, Mode } from 'core/slices/session';
import { IViewer, updatePlayerState, updateViewerState, ViewerState } from 'core/slices/player';
import { PayloadAction } from '@reduxjs/toolkit';
import { selectVerificationRequest } from 'core/selectors/product/verificationRequest';
import { IVerificationRequestDAO, VerificationRequestType } from 'types/domain/verification';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';

function* incDecSaga() {
  //yield updatePageIds();
}

function* viewStateSaga(action: PayloadAction<IViewer>) {
  if (action.payload.state === ViewerState.RESTING) yield updatePageIds();
}

export function* updatePageIds() {
  const { feed, index }: IActionFeed = yield select(selectActionFeed);
  const courseProgressId = yield select((state: RootState) => state.state[EntityTypes.COURSE_PROGRESS]);
  const sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
  const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);
  const moduleId = yield select((state: RootState) => state.state[EntityTypes.MODULE]);
  const courseNodeId = yield select((state: RootState) => state.state[EntityTypes.COURSE_NODE]);
  const mode: Mode = yield select(selectSessionMode);

  const page = feed[index];

  if (!page) return;

  if (mode === Mode.LEARNING) {
    if (page.sessionAttemptId && page.sessionAttemptId !== sessionAttemptId)
      yield put(yield call(updateState, { type: EntityTypes.SESSION_ATTEMPT, id: page.sessionAttemptId }));

    if (page.courseProgressId && page.courseProgressId !== courseProgressId)
      yield put(yield call(updateState, { type: EntityTypes.COURSE_PROGRESS, id: page.courseProgressId }));
  }
  if (page.moduleId !== moduleId && page.moduleId)
    yield put(yield call(updateState, { type: EntityTypes.MODULE, id: page.moduleId, culprit: 3 }));

  if (page.actionId !== actionId && page.actionId)
    yield put(yield call(updateState, { type: EntityTypes.ACTION, id: page.actionId, culprit: 3 }));

  //This adds too many things to queue
  // if (page.courseNodeId !== courseNodeId && page.courseNodeId)
  //   yield put(yield call(updateState, { type: EntityTypes.COURSE_NODE, id: page.courseNodeId, culprit: 3 }));

  if (page.verificationRequestId)
    yield all([
      put(yield call(updateState, { type: EntityTypes.MODULE, id: null })),
      put(yield call(updateState, { type: EntityTypes.ACTION, id: null })),
      put(yield call(updateState, { type: EntityTypes.SCENE, id: null })),
    ]);
}

function* nextSaga(action: PayloadAction<INext>) {
  const { feed, index }: IActionFeed = yield select(selectActionFeed);
  const isHistory = index < feed.length - 1;
  const token: IToken = yield select(selectToken);
  const page = feed[index];
  if (!page) return;

  if (page.verificationRequestId && !isHistory) {
    const verificationRegister: IVerificationRegister = yield select(state =>
      selectVerificationRegister(state, page.verificationRequestId),
    );
    const verificationRequestDAO: IVerificationRequestDAO = yield select(state =>
      selectVerificationRequest(state, page.verificationRequestId),
    );
    if (verificationRegister && !verificationRegister.saving)
      yield put(
        yield call(verificationRequest, {
          verificationRequestId:
            verificationRequestDAO.type === VerificationRequestType.REQUEST ? page.verificationRequestId : null,
          courseProgressId: token.courseProgressId,
          courseNodeToken: verificationRequestDAO.courseNodeToken,
          token: token.token,
          auto: true,
        }),
      );
  }
}

function* prevSaga() {
  const { feed, index }: IActionFeed = yield select(selectActionFeed);

  if (index > 0) yield put(yield call(decrementIndex));
  yield put(yield call(updatePlayerState, { paused: true, culprit: 'prevSaga' }));

  // yield removeSceneIfNotRequired(true);
}

function* watchPrev() {
  yield takeLatest(prev, prevSaga);
}

function* watchNext() {
  yield takeLatest(next, nextSaga);
}

function* watchPageChange() {
  yield takeLatest([decrementIndex], incDecSaga);
}

function* watchViewerState() {
  yield takeLatest(updateViewerState, viewStateSaga);
}

export function* pageSaga() {
  yield all([fork(watchPageChange), fork(watchNext), fork(watchPrev), fork(watchViewerState)]);
}

export function* waitForPageTransition() {
  const viewState: IViewer = yield select((state: RootState) => state.core.player.state.viewer);
  const actionFeed: IActionFeed = yield select(selectActionFeed);
  if (actionFeed.index > 0 && viewState.state === ViewerState.TRANSITIONING) yield take(updateViewerState);
}
